import { answerQuestions, useCountryList, useCountryListByProvinceAndState } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Toast from "@/components/Toast";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectArea from "@/components/Select";
import ValidateError from "@/containers/common/ValidateError";
import ContentContainer from "@/containers/common/ContentContainer";
import Input from "@/components/Input";

interface PlaceOfResidenceProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	onNextClick: (isChinaMainland?: boolean) => void;
}
interface IFormInput {
	countryOfResidence: string;
	provinceOfResidence: string;
	address: string;
}

function PlaceOfResidence(props: PlaceOfResidenceProps) {
	const router = useRouter();
	const { t } = useTranslation("survey");
	const [loading, setLoading] = useState(false);
	const { control, handleSubmit, setValue, watch, formState, getValues } = useForm<IFormInput>({
		mode: "onSubmit",
		defaultValues: {
			countryOfResidence: "",
			provinceOfResidence: "",
			address: "",
		},
	});

	const { isValid, errors } = formState;
	const selectedCountry = watch("countryOfResidence");

	const { countryList } = useCountryList({ language: router.locale });
	const { provinceAndStateList } = useCountryListByProvinceAndState({
		acronym: selectedCountry || "RF",
	});

	// 我们暂时不能为美国税务居民提供服务
	const unableToServe = useCallback(() => {
		return getValues("countryOfResidence") === "US";
	}, [getValues]);

	useEffect(() => {
		const _nationality = props.answerFieldToValueMap.get("nationality") || "";
		const _countryOfResidence = props.answerFieldToValueMap.get("countryOfResidence") || "";

		setValue("countryOfResidence", _countryOfResidence || _nationality);
		setValue("provinceOfResidence", props.answerFieldToValueMap.get("provinceOfResidence") || "");
		setValue("address", props.answerFieldToValueMap.get("address") || "", {
			shouldValidate: props.answerFieldToValueMap.get("address") ? true : false,
		});
	}, []);

	const onSubmit: SubmitHandler<IFormInput> = async (data) => {
		try {
			const countryQuestionId = props.questionFieldToIdMap.get("countryOfResidence");
			const provinceQuestionId = props.questionFieldToIdMap.get("provinceOfResidence");
			const addressQuestionId = props.questionFieldToIdMap.get("address");
			const isQuestionIdMissing = !countryQuestionId || !provinceQuestionId || !addressQuestionId;

			if (isQuestionIdMissing) throw Error("missing questionId");

			const payload: AnswerQuestionModel[] = [
				{
					answer: data.countryOfResidence,
					questionId: countryQuestionId,
				},
				{
					answer: data.provinceOfResidence,
					questionId: provinceQuestionId,
				},
				{
					answer: data.address,
					questionId: addressQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });

			const nationality = props.answerFieldToValueMap.get("nationality") || "";
			const isChinaMainland = nationality === "CN" || data.countryOfResidence === "CN";

			props.onNextClick(isChinaMainland);
		} catch (error: any) {
			Toast.fail(error.message);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_VIEW);
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			footer={
				<SurveyFooter
					disabled={!isValid}
					loading={loading}
					onNextBtnClick={handleSubmit(onSubmit)}
					onPrivacyClick={() => log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_PRAVICY)}
					onSecureClick={() => log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_SECURITY)}
				/>
			}
		>
			<SurveyTitle title={t("what-is-your-residential-address")} />
			<div className="h-12"></div>
			<Controller
				name="countryOfResidence"
				control={control}
				defaultValue=""
				rules={{ required: t("country-region-cannot-be-empty") }}
				render={({ field }) => (
					<SelectArea
						{...field}
						placeholder={t("country-region")}
						defaultValue={field.value}
						options={countryList}
						onChange={(value) => {
							field.onChange(value);
							log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_INPNATION);
						}}
					/>
				)}
			/>

			<ValidateError error={errors.countryOfResidence} />

			{unableToServe() ? (
				<p className="mt-4 text-medium text-xs dark:text-white">
					{t("due-to-regulatory-requirements-unable-to-serve-US-tax")}
					{t("please-confirm-your-tax-situation-before-depositing")}
				</p>
			) : null}

			<div className="h-2.5" />
			<Controller
				name="provinceOfResidence"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<SelectArea
						{...field}
						placeholder={t("state-province")}
						defaultValue={field.value}
						options={provinceAndStateList}
						onChange={(value) => {
							field.onChange(value);
							log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_INPPROVINCE);
						}}
					/>
				)}
			/>

			<div className="h-2.5" />
			<Controller
				name="address"
				control={control}
				defaultValue=""
				rules={{
					required: t("please-enter-the-correct-address"),
					maxLength: {
						value: 256,
						message: t("address-exceeds-maximum-length-limit"),
					},
				}}
				render={({ field }) => (
					<Input
						{...field}
						placeholder={t("address")}
						onClick={() => log2sensors(SensorsEventIds.AO_RESIDENTIALADRESS_INPADRESS)}
					/>
				)}
			/>

			<ValidateError error={errors.address} />
		</ContentContainer>
	);
}

export default PlaceOfResidence;
